<script setup lang="ts">
import { LAWYER_INFO } from '@/pages/lawyer/model/keys';
import DateSelector from '@/pages/lawyer/ui/sections/TimeSection/DateSelector.vue';
import TimeSelector from '@/entities/appointment/ui/TimeSelector/index.vue';
import TimeSlotsSkeleton from '@/entities/appointment/ui/TimeSlotsSkeleton/index.vue';
import { useConsultationFlow } from '@/features/consultationFlow/model/useConsultationFlow';
import { useThreeDays } from '@/pages/lawyer/ui/sections/TimeSection/composables/useThreeDays';
import { logError, normalizeError } from '@/shared/lib/errors';
import type { ILawyerDetail } from '@/entities/lawyer/model/types/lawyer';
import type { ITimeSlot, IScheduleDay } from '@/entities/appointment/model/types';

const isMobile: boolean = useIsMobile();
const lawyerInfo = inject(LAWYER_INFO);
const { $appointmentService } = useNuxtApp();
const { openConsultation: openConsultationModal } = useConsultationFlow();

const selectedTime: Ref<string> = ref('');
const isShowFree: Ref<boolean> = ref(true);
const manualDate: Ref<string> = ref('');
const manualSlots: Ref<ITimeSlot[] | null> = ref(null);
const isSlotsLoading: Ref<boolean> = ref(false);

const lawyer: ComputedRef<ILawyerDetail | null> = computed(
  () => lawyerInfo?.value ?? null,
);

const lawyerId: ComputedRef<number | undefined> = computed(() => lawyer.value?.id);
const { days, defaultSlots, pending: daysPending } = useThreeDays(lawyerId);

const defaultDay: ComputedRef<IScheduleDay | null> = computed(
  () => days.value?.[0] ?? null,
);

const selectedDate: ComputedRef<string> = computed(
  () => manualDate.value || defaultDay.value?.date || '',
);

const fetchDaySlots = async (date: string): Promise<ITimeSlot[]> => {
  const id = lawyer.value?.id;
  if (!id || !date) return [];
  const d = new Date(date);
  const { data } = await $appointmentService.getScheduleDay(
    id,
    d.getFullYear(),
    d.getMonth() + 1,
    d.getDate(),
  );
  return data ?? [];
};

const timestamps: ComputedRef<ITimeSlot[]> = computed(
  () => manualSlots.value ?? defaultSlots.value,
);

const isSelectedOnline: ComputedRef<boolean> = computed(() =>
  Boolean(days.value?.find((d) => d.date === selectedDate.value)?.is_online),
);

const freeConsultation = computed(() => ({
  text: isSelectedOnline.value
    ? 'В выбранное время дистанционная консультация бесплатна'
    : 'В выбранное время консультация в офисе бесплатна',
  icon: isSelectedOnline.value ? 'Laptop' : 'Office',
}));

const onSelectDate = async (payload: {
  date: string;
  isOnline: boolean;
}): Promise<void> => {
  if (!payload.date) return;

  if (payload.date === defaultDay.value?.date) {
    manualDate.value = '';
    manualSlots.value = null;
    isSlotsLoading.value = false;
    return;
  }
  if (payload.date === manualDate.value) return;

  manualDate.value = payload.date;
  isSlotsLoading.value = true;
  let slots: ITimeSlot[] = [];
  try {
    slots = await fetchDaySlots(payload.date);
  } catch (error) {
    logError(normalizeError(error), { action: 'lawyer-day-slots' });
  }
  if (manualDate.value !== payload.date) return;
  manualSlots.value = slots;
  isSlotsLoading.value = false;
};

const onSelectTime = (time: string): void => {
  isShowFree.value = false;
  selectedTime.value = time;
  window.setTimeout(() => {
    isShowFree.value = true;
  }, 250);
};

const openConsultation = (): void =>
  openConsultationModal({
    lawyerInfo: lawyer.value,
    date: selectedDate.value,
    time: selectedTime.value,
  });
</script>

<template>
  <div :class="['consultation-time', { _mobile: isMobile }]">
    <p class="consultation-time__title">Записаться на приём в офис</p>

    <div class="consultation-time__date">
      <DateSelector
        :lawyer="lawyer"
        :schedule-days="days ?? []"
        :pending="daysPending"
        @select="onSelectDate"
      />
    </div>

    <div class="consultation-time__time">
      <div class="consultation-time__time-title">Выберите удобное время</div>
      <TimeSlotsSkeleton v-if="isSlotsLoading" />
      <TimeSelector v-else :timestamps="timestamps" @select="onSelectTime" />
    </div>

    <div :class="['consultation-time__free', { _show: isShowFree }]">
      <AppIcon :name="freeConsultation.icon" class="consultation-time__icon" />
      {{ freeConsultation.text }}
    </div>

    <div class="consultation-time__button">
      <AppButton
        button-text="Записаться на консультацию"
        is-big
        @click="openConsultation"
      />
    </div>
  </div>
</template>

<style lang="less">
.consultation-time {
  box-shadow: 0px @size-x2 @size-x6 rgba(129, 135, 189, 0.15);
  padding: @size-x5;
  border-radius: @main-size;

  &__title {
    font-weight: 700;
    font-size: 18px;
    line-height: 130%;
    color: @color-elephant;
    margin: 0 0 15px;
  }

  &__time {
    margin-top: @size-x5;
  }

  &__time-title {
    color: @color-elephant;
    font-size: @size-x4;
    font-weight: 600;
    line-height: 130%;
    margin-bottom: 15px;
  }

  &__button {
    display: flex;
    justify-content: flex-end;
    margin-top: 30px;

    .app-button._big {
      width: auto;
      padding-left: @size-x6;
      padding-right: @size-x6;
    }

    .app-button__text {
      white-space: nowrap;
    }
  }

  &__free {
    display: inline-flex;
    align-items: center;
    gap: @main-size;
    color: @color-mountain-meadow;
    margin-top: @size-x4;
    font-size: 13px;
    line-height: 17px;
    font-weight: 500;
    opacity: 0;
    transition: opacity @A ease-in-out;

    &._show {
      opacity: 1;
    }
  }

  &__icon path {
    fill: @color-mountain-meadow;
  }

  &._mobile {
    position: relative;
    padding: @size-x6 @size-x4;
  }

  &._mobile &__title {
    font-size: @size-x4;
    line-height: 22px;
    margin-bottom: 26px;
    width: calc(100% - 65px);
  }

  &._mobile &__time-title {
    font-size: 14px;
    line-height: 19px;
  }

  &._mobile &__time {
    margin-top: 15px;
  }

  &._mobile &__button {
    margin-top: @size-x6;
  }
}
</style>
